import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Navigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { getProgram, MEMBER_TYPES, PROGRAMS } from '../../config/programs';
import { CHESS_LEVELS } from '../../utils/reportSchema';
import { fetchClubs, fetchMember, fetchMembers, saveMember, resolveAuthorName } from '../../utils/programData';
import { normalizeName } from '../../utils/memberDedupe';
import { ProgramShell, S, T } from './ui';

const EMPTY = {
  firstName: '', lastName: '', type: '', gender: '', dob: '', phone: '',
  school: '', className: '', area: '', centre: '',
  parentName: '', parentPhone: '',
  clubId: '', clubName: '', chessLevel: 'Mchipukizi',
  hasCapital: false, businessName: '', learnsChess: false,
  notes: '', programIds: [],
};

export default function MemberForm() {
  const { pid, mid } = useParams();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const program = getProgram(pid);
  const [form, setForm] = useState(() => {
    const type = program?.memberTypes[0] || 'mtoto';
    const programIds = pid === 'msaada' && type === 'mtoto' ? [pid, 'chess'] : [pid];
    return { ...EMPTY, type, programIds };
  });
  const [clubs, setClubs] = useState([]);
  const [existingMembers, setExistingMembers] = useState([]);
  const [confirmedDifferent, setConfirmedDifferent] = useState(false);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [pid, mid]);

  async function load() {
    try {
      setClubs(await fetchClubs(pid));
      if (mid) {
        const m = await fetchMember(mid);
        if (m) setForm({ ...EMPTY, ...m });
      } else {
        // Kwa usajili mpya tu — hii ndiyo inayowezesha kugundua marudio
        setExistingMembers(await fetchMembers(pid));
      }
    } catch (e) { console.error(e); }
  }

  if (!program) return <Navigate to="/programs" />;

  // Mtu mwenye jina na aina moja ndani ya programu hii, ambaye si mwenyewe
  const fullNameNow = `${form.firstName} ${form.lastName}`.trim();
  const duplicateMatches = (!mid && fullNameNow)
    ? existingMembers.filter(m => m.type === form.type && normalizeName(m.fullName) === normalizeName(fullNameNow))
    : [];

  function change(k, v) {
    setForm(f => {
      const next = { ...f, [k]: v };
      // Mtoto wa Msaada anaingia moja kwa moja kwenye Programu ya Chess —
      // taarifa zake zinachukuliwa mara moja tu.
      if (k === 'type' && pid === 'msaada' && v === 'mtoto') {
        const list = next.programIds || [];
        if (!list.includes('chess')) next.programIds = [...list, 'chess'];
      }
      return next;
    });
    // Jina likibadilika, uthibitisho wa zamani wa "ni mtu tofauti"
    // hauhusiani tena — lazima uangaliwe upya.
    if (k === 'firstName' || k === 'lastName' || k === 'type') setConfirmedDifferent(false);
  }

  function toggleProgram(id) {
    setForm(f => {
      const list = f.programIds || [];
      if (id === pid) return f; // programu ya sasa haiwezi kuondolewa hapa
      return { ...f, programIds: list.includes(id) ? list.filter(x => x !== id) : [...list, id] };
    });
  }

  async function submit() {
    if (!form.firstName.trim()) { setErr('Jina la kwanza linahitajika.'); return; }
    if (!form.type) { setErr('Chagua aina ya mwanachama.'); return; }
    if (duplicateMatches.length > 0 && !confirmedDifferent) {
      setErr('Tayari kuna mwanachama mwenye jina hili. Angalia taarifa hapo chini kabla ya kuendelea.');
      return;
    }
    setSaving(true); setErr('');
    try {
      const name = await resolveAuthorName(currentUser);
      const club = clubs.find(c => c.id === form.clubId);
      const programIds = Array.from(new Set([...(form.programIds || []), pid]));
      const payload = {
        ...form,
        programIds,
        fullName: `${form.firstName} ${form.lastName}`.trim(),
        nameKey: normalizeName(`${form.firstName} ${form.lastName}`),
        clubName: club ? club.name : '',
        createdBy: currentUser.uid,
        createdByName: name,
      };
      const id = await saveMember(payload, mid);
      navigate(`/programs/${pid}/members/${id}`);
    } catch (e) {
      console.error(e);
      setErr('Imeshindikana kuhifadhi. Angalia mtandao kisha jaribu tena.');
    }
    setSaving(false);
  }

  const identify = MEMBER_TYPES[form.type]?.identify;

  return (
    <ProgramShell program={program} tab="members">
      <div style={S.card}>
        <h2 style={S.h2}>{mid ? 'Badilisha Mwanachama' : 'Sajili Mwanachama Mpya'}</h2>
        {err && <div style={S.err}>{err}</div>}

        <label style={S.label}>Aina ya mwanachama *</label>
        <div style={{ display: 'flex', gap: '8px', marginBottom: '18px', flexWrap: 'wrap' }}>
          {program.memberTypes.map(t => (
            <button
              key={t}
              onClick={() => change('type', t)}
              style={{
                padding: '10px 18px', borderRadius: '99px', cursor: 'pointer',
                border: `1px solid ${form.type === t ? program.color : T.line}`,
                background: form.type === t ? program.color : '#fff',
                color: form.type === t ? '#fff' : T.muted,
                fontSize: '13px', fontWeight: 700, fontFamily: 'inherit',
              }}
            >{MEMBER_TYPES[t].label}</button>
          ))}
        </div>

        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
          <div style={{ flex: '1 1 150px' }}>
            <label style={S.label}>Jina la kwanza *</label>
            <input style={{ ...S.input, marginBottom: '16px' }} value={form.firstName}
              onChange={e => change('firstName', e.target.value)} />
          </div>
          <div style={{ flex: '1 1 150px' }}>
            <label style={S.label}>Jina la mwisho</label>
            <input style={{ ...S.input, marginBottom: '16px' }} value={form.lastName}
              onChange={e => change('lastName', e.target.value)} />
          </div>
        </div>

        {/* ── Tahadhari ya marudio — mtu mwenye jina hili tayari yupo ── */}
        {duplicateMatches.length > 0 && (
          <div style={{
            background: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: '13px',
            padding: '15px', marginBottom: '18px',
          }}>
            <div style={{ display: 'flex', gap: '9px', alignItems: 'flex-start', marginBottom: '11px' }}>
              <span style={{ fontSize: '15px' }}>⚠️</span>
              <div style={{ fontSize: '13px', fontWeight: 800, color: '#92400E' }}>
                Tayari ameshasajiliwa — {duplicateMatches.length === 1 ? 'mtu mmoja mwenye jina hili yupo' : `watu ${duplicateMatches.length} wenye jina hili wapo`}
              </div>
            </div>
            {duplicateMatches.map(m => (
              <div key={m.id} style={{
                background: '#fff', borderRadius: '10px', padding: '10px 12px', marginBottom: '8px',
                display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '10px',
              }}>
                <div style={{ fontSize: '12px', color: '#374151', lineHeight: 1.6 }}>
                  {m.school || m.centre || m.area || '—'}
                  {m.clubName ? ` · ${m.clubName}` : ''}
                  <br />
                  <span style={{ color: '#92400E' }}>alisajiliwa na {m.createdByName || 'staff'}</span>
                </div>
                <button
                  type="button"
                  onClick={() => navigate(`/programs/${pid}/members/${m.id}`)}
                  style={{ ...S.btnGhost, padding: '7px 12px', fontSize: '11px', whiteSpace: 'nowrap' }}
                >
                  Ona wasifu
                </button>
              </div>
            ))}
            <label style={{
              display: 'flex', alignItems: 'center', gap: '9px', cursor: 'pointer',
              fontSize: '12.5px', color: '#92400E', marginTop: '9px',
            }}>
              <input type="checkbox" checked={confirmedDifferent}
                onChange={e => setConfirmedDifferent(e.target.checked)}
                style={{ width: '16px', height: '16px' }} />
              Hapana, huyu ni mtu tofauti — endelea kumsajili
            </label>
          </div>
        )}

        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
          <div style={{ flex: '1 1 140px' }}>
            <label style={S.label}>Jinsia</label>
            <select style={{ ...S.input, marginBottom: '16px' }} value={form.gender}
              onChange={e => change('gender', e.target.value)}>
              <option value="">— Chagua —</option>
              <option value="Me">Me</option>
              <option value="Ke">Ke</option>
            </select>
          </div>
          <div style={{ flex: '1 1 140px' }}>
            <label style={S.label}>Tarehe ya kuzaliwa</label>
            <input type="date" style={{ ...S.input, marginBottom: '16px' }} value={form.dob}
              onChange={e => change('dob', e.target.value)} />
          </div>
        </div>

        {/* ── Utambulisho: mtoto=shule, kijana=kituo, mtu mzima/mzazi=eneo ── */}
        {identify === 'school' && (
          <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
            <div style={{ flex: '2 1 180px' }}>
              <label style={S.label}>Shule *</label>
              <input style={{ ...S.input, marginBottom: '16px' }} value={form.school}
                onChange={e => change('school', e.target.value)} placeholder="mf. Shule ya Msingi Kaloleni" />
            </div>
            <div style={{ flex: '1 1 100px' }}>
              <label style={S.label}>Darasa</label>
              <input style={{ ...S.input, marginBottom: '16px' }} value={form.className}
                onChange={e => change('className', e.target.value)} placeholder="mf. 5" />
            </div>
          </div>
        )}

        {identify === 'centre' && (
          <>
            <label style={S.label}>Kituo *</label>
            <input style={{ ...S.input, marginBottom: '16px' }} value={form.centre}
              onChange={e => change('centre', e.target.value)}
              placeholder="mf. Usa River Youth Development Centre" />
          </>
        )}

        {identify === 'area' && (
          <>
            <label style={S.label}>Eneo / Mtaa *</label>
            <input style={{ ...S.input, marginBottom: '16px' }} value={form.area}
              onChange={e => change('area', e.target.value)} placeholder="mf. Sombetini, Arusha" />
          </>
        )}

        <label style={S.label}>Namba ya simu</label>
        <input style={{ ...S.input, marginBottom: '16px' }} value={form.phone}
          onChange={e => change('phone', e.target.value)} placeholder="+255..." />

        {(form.type === 'mtoto' || form.type === 'kijana') && (
          <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
            <div style={{ flex: '1 1 150px' }}>
              <label style={S.label}>Jina la mzazi / mlezi</label>
              <input style={{ ...S.input, marginBottom: '16px' }} value={form.parentName}
                onChange={e => change('parentName', e.target.value)} />
            </div>
            <div style={{ flex: '1 1 150px' }}>
              <label style={S.label}>Simu ya mzazi</label>
              <input style={{ ...S.input, marginBottom: '16px' }} value={form.parentPhone}
                onChange={e => change('parentPhone', e.target.value)} />
            </div>
          </div>
        )}

        {form.type === 'mzazi' && (
          <>
            <label style={{ display: 'flex', alignItems: 'center', gap: '9px', cursor: 'pointer', fontSize: '13.5px', color: '#374151', marginBottom: '6px' }}>
              <input type="checkbox" checked={!!form.learnsChess}
                onChange={e => change('learnsChess', e.target.checked)}
                style={{ width: '17px', height: '17px' }} />
              Anajifunza chess (hiari)
            </label>
            <div style={{ fontSize: '11.5px', color: T.muted, marginBottom: '16px', lineHeight: 1.5 }}>
              Kwa wazazi chess si lazima. Bila alama hii, ripoti yake haitakuwa na
              maswali ya chess — itakuwa lifeskills, microfinance na maendeleo binafsi pekee.
            </div>
            <label style={{ display: 'flex', alignItems: 'center', gap: '9px', cursor: 'pointer', fontSize: '13.5px', color: '#374151', marginBottom: '14px' }}>
              <input type="checkbox" checked={!!form.hasCapital}
                onChange={e => change('hasCapital', e.target.checked)}
                style={{ width: '17px', height: '17px' }} />
              Amepewa mtaji tayari
            </label>
            <div style={{ fontSize: '11.5px', color: T.muted, marginTop: '-8px', marginBottom: '14px', lineHeight: 1.5 }}>
              Ukiweka alama hapa, ripoti yake ya wiki itaonyesha maswali ya biashara. Vinginevyo hayataonekana.
            </div>
            {form.hasCapital && (
              <>
                <label style={S.label}>Jina la biashara</label>
                <input style={{ ...S.input, marginBottom: '16px' }} value={form.businessName}
                  onChange={e => change('businessName', e.target.value)} />
              </>
            )}
          </>
        )}

        {/* ── Chess club — kwa mzazi inaonekana tu akijifunza chess ── */}
        {(form.type !== 'mzazi' || form.learnsChess) && (
        <>
        <label style={S.label}>Club ya chess</label>
        <select style={{ ...S.input, marginBottom: '16px' }} value={form.clubId}
          onChange={e => change('clubId', e.target.value)}>
          <option value="">— Hana club bado —</option>
          {clubs.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        {clubs.length === 0 && (
          <div style={{ fontSize: '11.5px', color: T.muted, marginTop: '-8px', marginBottom: '16px' }}>
            Hakuna club bado kwenye programu hii — tengeneza kwenye tab ya "Vilabu".
          </div>
        )}

        <label style={S.label}>Ngazi ya chess</label>
        <select style={{ ...S.input, marginBottom: '16px' }} value={form.chessLevel}
          onChange={e => change('chessLevel', e.target.value)}>
          {CHESS_LEVELS.map(l => <option key={l} value={l}>{l}</option>)}
        </select>
        </>
        )}

        {/* ── Programu nyingine ── */}
        <label style={S.label}>Yuko pia kwenye programu zipi?</label>
        <div style={{ display: 'flex', gap: '8px', marginBottom: '16px', flexWrap: 'wrap' }}>
          {PROGRAMS.map(p => {
            const on = (form.programIds || []).includes(p.id);
            const locked = p.id === pid;
            return (
              <button
                key={p.id}
                onClick={() => toggleProgram(p.id)}
                style={{
                  padding: '9px 15px', borderRadius: '99px',
                  cursor: locked ? 'default' : 'pointer',
                  border: `1px solid ${on ? p.color : T.line}`,
                  background: on ? p.color : '#fff',
                  color: on ? '#fff' : T.muted, opacity: locked ? 0.85 : 1,
                  fontSize: '12px', fontWeight: 700, fontFamily: 'inherit',
                }}
              >{p.short}{locked ? ' •' : ''}</button>
            );
          })}
        </div>
        <div style={{ fontSize: '11.5px', color: T.muted, marginTop: '-8px', marginBottom: '18px', lineHeight: 1.5 }}>
          Mtoto wa Msaada ambaye pia yuko chess club anasajiliwa MARA MOJA tu.
          {pid === 'msaada' && form.type === 'mtoto' &&
            ' Programu ya Chess imewekwa moja kwa moja — huna haja ya kuchukua taarifa zake tena.'}
        </div>

        <label style={S.label}>Maelezo mengine</label>
        <textarea style={{ ...S.input, minHeight: '80px', marginBottom: '20px', resize: 'vertical' }}
          value={form.notes} onChange={e => change('notes', e.target.value)} />

        <div style={{ display: 'flex', gap: '10px' }}>
          <button style={{ ...S.btn, background: program.color, opacity: saving ? 0.6 : 1, flex: 1 }}
            disabled={saving} onClick={submit}>
            {saving ? 'Inahifadhi...' : mid ? 'Hifadhi mabadiliko' : 'Sajili'}
          </button>
          <button style={S.btnGhost} onClick={() => navigate(`/programs/${pid}/members`)}>Ghairi</button>
        </div>
      </div>
    </ProgramShell>
  );
}
